import { useDatabaseContent } from '@databases/database-state';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { AsyncOperationStatus } from '@databases/redux-types';
import {
  type ResolvedContentReference,
  type PageReferenceOrContentId,
  type ResolvedPageReference,
} from '@databases/types/confluence';
import { type DatabaseField } from '@databases/types/database';
import { pageToReference } from '@databases/utils/confluence/utils';
import { getDatabaseEntryOptional } from '@databases/utils/database/utils';
import { loadContentById, loadContentRef } from '@databases/utils/redux/content/actions';
import {
  selectContentByIdOptional,
  selectContentByIdStatus,
  selectContentRefOptional,
  selectContentRefStatus,
} from '@databases/utils/redux/content/selectors';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { useCallback } from 'react';

export function useDynamicParent(): {
  isPending: boolean;
  parentRef: ResolvedContentReference | undefined;
} {
  const dispatch = defaultStoreUtils.useDispatch();

  const { parentContentId } = useDatabaseContent();
  const contentId = parentContentId ?? undefined;

  useCallbackEffect(
    useCallback(async () => {
      if (contentId) {
        await dispatch(loadContentById(contentId));
      }
    }, [contentId, dispatch])
  );

  const [isPending, parentRef] = defaultStoreUtils.useEqualSelector((state) => {
    const parentItemById = selectContentByIdOptional(state, {
      contentId,
    });
    const parentItemByIdStatus = selectContentByIdStatus(state, {
      contentId,
    });
    const parentItemByIdIsPending =
      !!contentId &&
      [undefined, AsyncOperationStatus.INITIAL, AsyncOperationStatus.PENDING].includes(
        parentItemByIdStatus?.status
      );

    const spaceKey = parentItemById?.space?.key;

    return [
      parentItemByIdIsPending,
      parentItemById && parentItemById.type !== 'blogpost' && spaceKey
        ? {
            type: parentItemById.type,
            spaceKey,
            contentTitle: parentItemById.title,
            contentId: parentItemById.id,
          }
        : undefined,
    ] as const;
  });
  return { isPending, parentRef };
}

/**
 * This hook returns a page reference to the configured parent page of this field. Returns undefined if no valid parent page is configured,
 * or if the reference is still loading (because a referenced database has to be loaded).
 */
function useParentPageReference({
  contentId,
  field,
  entryId,
}: {
  contentId?: string;
  field: DatabaseField;
  entryId?: string;
}): {
  isPending: boolean;
  pageRef: PageReferenceOrContentId | undefined;
} {
  return defaultStoreUtils.useEqualSelector((state) => {
    const database =
      contentId != null
        ? selectDatabaseByContentIdRequired(state, {
            contentId,
          })
        : undefined;
    const entry =
      database && entryId != null
        ? getDatabaseEntryOptional(database, entryId)
        : undefined;

    if (!database || !entry || field.settings.creatable === false) {
      return { isPending: false, pageRef: undefined };
    }

    return { isPending: false, pageRef: field.settings.parentPage };
  });
}

/**
 * This hook returns the resolved page reference that should be used as the parent page for new pages created by this field. It
 * returns undefined if no valid page is configured or if the page is still loading. It makes sure to load the page if it is not
 * loaded yet.
 */
export function useFixedParentPage({
  contentId,
  field,
  entryId,
}: {
  contentId?: string;
  field: DatabaseField;
  entryId?: string;
}): { isPending: boolean; pageRef: ResolvedPageReference | undefined } {
  const { isPending: parentPageIsPending, pageRef: parentPageRef } =
    useParentPageReference({ contentId, field, entryId });

  const dispatch = defaultStoreUtils.useDispatch();

  useCallbackEffect(
    useCallback(async () => {
      if (parentPageRef && 'contentId' in parentPageRef && parentPageRef.contentId) {
        await dispatch(loadContentById(parentPageRef.contentId));
      } else if (parentPageRef && 'contentTitle' in parentPageRef) {
        await dispatch(loadContentRef(parentPageRef));
      }
    }, [dispatch, parentPageRef])
  );

  const [isPending, pageRef] = defaultStoreUtils.useEqualSelector((state) => {
    const parentPageById = selectContentByIdOptional(state, {
      contentId:
        parentPageRef && 'contentId' in parentPageRef
          ? parentPageRef.contentId
          : undefined,
    });
    const parentPageByIdStatus = selectContentByIdStatus(state, {
      contentId:
        parentPageRef && 'contentId' in parentPageRef
          ? parentPageRef.contentId
          : undefined,
    });

    const parentPageByTitle = selectContentRefOptional(state, {
      contentRef:
        parentPageRef && 'contentTitle' in parentPageRef ? parentPageRef : undefined,
    });
    const parentPageByTitleStatus = selectContentRefStatus(state, {
      contentRef:
        parentPageRef && 'contentTitle' in parentPageRef ? parentPageRef : undefined,
    });

    const parentPageByIdIsPending =
      !!parentPageRef &&
      'contentId' in parentPageRef &&
      [undefined, AsyncOperationStatus.INITIAL, AsyncOperationStatus.PENDING].includes(
        parentPageByIdStatus?.status
      );
    const parentPageByTitleIsPending =
      !!parentPageRef &&
      'contentTitle' in parentPageRef &&
      [undefined, AsyncOperationStatus.INITIAL, AsyncOperationStatus.PENDING].includes(
        parentPageByTitleStatus?.status
      );

    return [
      parentPageIsPending || parentPageByIdIsPending || parentPageByTitleIsPending,
      parentPageById && parentPageById.type === 'page'
        ? pageToReference(parentPageById)
        : parentPageByTitle?.type === 'page'
        ? parentPageByTitle
        : undefined,
    ] as const;
  });

  return { isPending, pageRef };
}
